import { type ComponentType, lazy, Suspense } from 'react';
import { gameHome } from './shell/paths';
import { Shell } from './shell/Shell';

/**
 * Dev pages live in their own chunk (vite.config `dev`): never in the shipped boot path.
 *
 * They are resolved through `import.meta.glob` rather than a bare `import()` so a route can be
 * registered before its module lands: an entry whose file does not exist yet simply has no route,
 * instead of failing the whole production build with UNRESOLVED_IMPORT. Same pattern as
 * `shell/screens/TutorialGate.tsx`.
 */
const devModules = import.meta.glob<Record<string, unknown>>('./dev/*.tsx');

function devPage(file: string, named: string) {
  const loader = devModules[`./dev/${file}.tsx`];
  if (!loader) return null;
  return lazy(async () => {
    const m = await loader();
    const C = (m[named] ?? m['default']) as ComponentType | undefined;
    if (!C) throw new Error(`dev page ${file} has no ${named} export`);
    return { default: C };
  });
}

const DEV_PAGES: Record<string, ReturnType<typeof devPage>> = {
  '/dev/layout': devPage('LayoutLab', 'LayoutLab'),
  '/dev/juice': devPage('JuicePlayground', 'JuicePlayground'),
  '/dev/table': devPage('TablePlayground', 'TablePlayground'),
  '/dev/demo': devPage('Demo', 'Demo'),
  // --- diorama/seats/foil agent (C-T10/T11/T17): background + diorama + seats lab ------------
  '/dev/diorama': devPage('DioramaLab', 'DioramaLab'),
  // --- diorama/seats/foil agent (C-T14/T15): the bidding set piece --------------------------
  '/dev/bid': devPage('BidLab', 'BidLab'),
  // --- end diorama/seats/foil agent ----------------------------------------------------------
  // Design lab: menu / onboarding / in-game mocks on real components, no server (docs/DESIGN-LAB.md).
  '/dev/design': devPage('DesignLab', 'DesignLab'),
  '/dev/design-stage': devPage('DesignStage', 'DesignStage'),
};

/**
 * Are the `/dev/*` labs reachable on this load?
 *
 * They stay in the production bundle on purpose — the e2e specs and the capture tooling drive the
 * built app, not a dev server — but a player who finds `/dev/table` on the live site should get
 * the game, not a debug harness with a scene inspector attached. So the routes are registered only
 * behind `?e2e=1` (which every spec and both capture harnesses already pass) or in a real dev
 * build. Anything else falls through to the shell, which routes `/dev/…` to the game (`/play` on
 * the web) like any other unknown path.
 */
export function devRoutesEnabled(search: string = location.search, isDev: boolean = import.meta.env.DEV): boolean {
  return isDev || new URLSearchParams(search).get('e2e') === '1';
}

/** Is this a `/dev/*` path at all, registered or not? */
export const isDevPath = (path: string): boolean => path === '/dev' || path.startsWith('/dev/');

export function App() {
  const path = location.pathname.replace(/\/+$/, '') || '/';
  const enabled = devRoutesEnabled();

  // Rewrite the address rather than quietly serving the game from a /dev URL: the player should
  // end up somewhere they can bookmark and share, and a stray /dev link should stop existing the
  // moment it is followed. `replaceState` keeps it out of the back button.
  if (!enabled && isDevPath(path)) {
    try {
      history.replaceState(null, '', `${gameHome()}${location.search}${location.hash}`);
    } catch {
      /* history blocked; the shell still renders the game */
    }
  }

  const Dev = enabled ? DEV_PAGES[path] : null;
  if (Dev) {
    return (
      <Suspense fallback={<div data-testid="dev-loading" />}>
        <Dev />
      </Suspense>
    );
  }
  return <Shell />;
}
